import Axios from 'axios';
import { ICreatePageDTO } from '../dtos/create-page-dto';
import { ILoadDocumentListPayload } from '../types/load-document-list-payload';
import { IPublishDocumentPayload } from '@/document-view/types/publish-document-payload';
import { DocumentQueries } from '@/api/documents/graphql/document.queries';
import { DocumentMutations } from '@/api/documents/graphql/document.mutations';
import { IDocumentTracking } from '@/document-view/tracking/types/document-tracking';
import { apolloClient } from '@/apollo';
import { IDocument } from '@lib-shared/types/Document';
import { IDocumentPage } from '@lib-shared/types/document-page';
import { IEditorRecipientInput } from '@lib-shared/types/editor-recipient-input';
import { ISession } from '@/types/Session';
import { IPreviewToken } from '@/document-edit/assembler/document/types/preview-token';
import { IUploadFileUrl } from '@/types/upload-file-url';
import { IDocumentRevision } from '@lib-shared/types/document-revision';
import { IUpdatePageDTO } from '@/api/documents/dtos/update-page-dto';
import { EFetchPolicy } from '@/api/types/enums/fetch-policy';
import { inProduction } from '@/common/helpers';
import { IUpdateDocument } from '@/document-edit/assembler/document/types/update-document';
import { DocumentHelper } from '@/api/documents/helpers/document.helper';
import { IDocumentNotification } from '@lib-shared/types/DocumentNotification';
import { IDocumentNotificationPayload } from '@lib-shared/types/document-notification-payload';

export class DocumentService {
  private static readonly documentIdFormName = 'document-id';
  private static readonly saveDocumentPath = '/save/document';

  static async loadDocument(documentId: string): Promise<IDocument> {
    const {
      data: { document },
    } = await apolloClient.query({
      query: DocumentQueries.documentQuery,
      variables: { documentId },
      fetchPolicy: EFetchPolicy.NetworkOnly,
    });
    return DocumentHelper.correctDates(document);
  }

  static updateDocument = async (payload: IUpdateDocument, documentId: string): Promise<string> => {
    const { data } = await apolloClient.mutate({
      mutation: DocumentMutations.updateDocumentMutation,
      variables: { ...DocumentHelper.correctDatesUpdate(payload), documentId },
    });
    return data.updateDocument.id;
  };

  static async loadDocumentRevisions(documentId: string): Promise<IDocumentRevision[]> {
    const {
      data: {
        document: { documentRevisions },
      },
    } = await apolloClient.query({
      query: DocumentQueries.documentRevisionsQuery,
      variables: { documentId },
      fetchPolicy: EFetchPolicy.NetworkOnly,
    });
    return documentRevisions;
  }

  static async loadEditorRecipientInput(documentId: string): Promise<IEditorRecipientInput[]> {
    const {
      data: { editorRecipientInput },
    } = await apolloClient.query({
      query: DocumentQueries.editorRecipientInputQuery,
      variables: { documentId },
      fetchPolicy: EFetchPolicy.NetworkOnly,
    });
    return editorRecipientInput;
  }

  static async loadDocumentPage(documentId: string, id: string): Promise<IDocumentPage> {
    const {
      data: { documentPage },
    } = await apolloClient.query({
      query: DocumentQueries.loadDocumentPageQuery,
      variables: { documentId, id },
      fetchPolicy: EFetchPolicy.NetworkOnly,
    });
    return documentPage;
  }

  static async publishDocument({ documentId, message }: IPublishDocumentPayload): Promise<boolean> {
    const {
      data: { publishDocument },
    } = await apolloClient.mutate({
      mutation: DocumentMutations.publishDocumentMutation,
      variables: { documentId, message },
    });
    return publishDocument;
  }

  static async loadPages(documentId: string): Promise<IDocumentPage[]> {
    const {
      data: { document },
    } = await apolloClient.query({
      query: DocumentQueries.documentPageQuery,
      variables: { documentId },
      fetchPolicy: EFetchPolicy.NetworkOnly,
    });
    return document.pages;
  }

  static async discardChanges(documentId: string): Promise<boolean> {
    const {
      data: { discardChanges },
    } = await apolloClient.mutate({
      mutation: DocumentMutations.discardChangesMutation,
      variables: { documentId },
    });
    return discardChanges;
  }

  static async loadPageThumbUrl(
    documentPageId: string,
    documentId: string
  ): Promise<Partial<IDocumentPage>> {
    const {
      data: { documentPage },
    } = await apolloClient.query({
      query: DocumentQueries.pageThumbUrl,
      variables: { id: documentPageId, documentId },
      fetchPolicy: EFetchPolicy.CacheFirst,
    });
    return documentPage;
  }

  static async createSampleDocument(session: ISession, templateId?: string): Promise<string> {
    const payload = new FormData();
    let fallbackTempId = '5qdv4u5vrm2x';
    if (!inProduction(window)) {
      fallbackTempId = 'n2f9n5v5';
    }
    payload.append(this.documentIdFormName, templateId ? templateId : fallbackTempId);
    const {
      data: { document_id, error },
    } = await Axios.post('/get/sample/document', payload);
    if (error) {
      throw new Error(error);
    }
    if (!session) {
      return;
    }
    if (document_id) {
      let firstRoleId;
      if (templateId !== fallbackTempId) {
        const document: IDocument = await DocumentService.loadDocument(document_id);
        const firstRole = document?.recipients?.find(recipient => recipient.roleName !== '');
        firstRoleId = firstRole?.id;
      }
      const body = new FormData();
      body.append('action', 'save');
      body.append(this.documentIdFormName, document_id);
      body.append('recipient-email', session.email || '');
      body.append('recipient-first_name', session.first_name || '');
      body.append('recipient-last_name', session.last_name || '');
      body.append('recipient-role', 'signer');
      if (firstRoleId) {
        body.append('recipient-id', firstRoleId);
      }
      await Axios.post('/save/recipient', body);
    }

    return document_id;
  }

  static async loadTracking(documentId: string): Promise<IDocumentTracking> {
    const {
      data: { document },
    } = await apolloClient.query({
      query: DocumentQueries.documentTrackingQuery,
      variables: { documentId },
      fetchPolicy: EFetchPolicy.NetworkOnly,
    });
    return document;
  }

  static async getPreviewToken(documentId: string): Promise<IPreviewToken> {
    const {
      data: { previewToken },
    } = await apolloClient.query({
      query: DocumentQueries.documentPreviewTokenQuery,
      variables: { documentId },
      fetchPolicy: EFetchPolicy.NoCache,
    });
    return previewToken;
  }

  static async createDocument(documentType: string): Promise<string> {
    const body = new FormData();
    body.append('action', 'data');
    body.append('document-type', documentType);
    const {
      data: { document_id },
    } = await Axios.post(this.saveDocumentPath, body);
    return document_id;
  }

  static async loadPageNums(documentId: string): Promise<Partial<IDocumentPage>[]> {
    const {
      data: {
        document: { pages },
      },
    } = await apolloClient.query({
      query: DocumentQueries.pageNumQuery,
      variables: { documentId },
      fetchPolicy: EFetchPolicy.NoCache,
    });
    return pages;
  }

  static async loadTemplateResourceDetails(contentId: string): Promise<Partial<IDocument>> {
    const {
      data: { document },
    } = await apolloClient.query({
      query: DocumentQueries.templateResourceDetailsQuery,
      variables: { contentId },
    });
    return document;
  }

  static async createPage(payload: ICreatePageDTO) {
    const {
      data: { addDocumentPage },
    } = await apolloClient.mutate({
      mutation: DocumentMutations.addDocumentPageMutation,
      variables: { ...payload },
    });
    return addDocumentPage;
  }

  static async deletePage(documentId: string, documentPageId: string): Promise<boolean> {
    const { data } = await apolloClient.mutate({
      mutation: DocumentMutations.deleteDocumentPageMutation,
      variables: { documentId, documentPageId },
    });
    return data;
  }

  static async delete(documentId: string): Promise<boolean> {
    const body = new FormData();

    body.append('action', 'delete');
    body.append(this.documentIdFormName, documentId);

    const { data } = await Axios.post('/save/board', body);
    if (data.error) {
      throw new Error(data.error);
    }
    if (data.status !== 1) {
      throw new Error(data);
    }
    return !!data.status;
  }

  static async updatePage(dto: IUpdatePageDTO): Promise<IDocumentPage> {
    const { data } = await apolloClient.mutate({
      mutation: DocumentMutations.updateDocumentPageMutation,
      variables: dto,
    });
    return data.updateDocumentPage;
  }

  static async useTemplate(
    documentId: string,
    templateId: string,
    pageOffset: number
  ): Promise<IDocument> {
    const {
      data: { implementTemplateInDocument },
    } = await apolloClient.mutate({
      mutation: DocumentMutations.implementTemplateInDocumentMutation,
      variables: {
        documentId,
        templateId,
        pageOffset,
      },
    });
    return implementTemplateInDocument;
  }

  static async uploadFiles(uploadFileUrls: IUploadFileUrl[], files: File[]): Promise<void> {
    await Promise.all(
      uploadFileUrls.map(async ({ uploadUrl }: IUploadFileUrl, index: number) => {
        return Axios.put(uploadUrl, files[index]);
      })
    );
  }

  static async loadExternalEditorId(documentId: string): Promise<string> {
    const {
      data: {
        document: { externalEditorId },
      },
    } = await apolloClient.query({
      query: DocumentQueries.externalEditorIdQuery,
      variables: { documentId },
      fetchPolicy: EFetchPolicy.NoCache,
    });
    return externalEditorId;
  }

  static async sortDocumentPages(
    documentId: string,
    documentPageIds: string[],
    pageNums: number[]
  ): Promise<IDocumentPage[]> {
    const { data } = await apolloClient.mutate({
      mutation: DocumentMutations.sortDocumentPagesMutation,
      variables: {
        documentId,
        documentPageIds,
        pageNums,
      },
    });
    return data.sortDocumentPages;
  }

  static async sealDocument(documentId: string, sendDocument: boolean): Promise<void> {
    await apolloClient.mutate({
      mutation: DocumentMutations.sealDocumentMutation,
      variables: {
        documentId,
        sendDocument,
      },
    });
  }

  static async deleteTemplate(id: string): Promise<void> {
    const body: FormData = new FormData();
    body.append('action', 'delete');
    body.append(this.documentIdFormName, id);
    await Axios.post('/save/board', body);
  }

  static async updateSignature(signature: string): Promise<void> {
    await apolloClient.mutate({
      mutation: DocumentMutations.updateSignatureMutation,
      variables: {
        signature,
      },
    });
  }

  static async loadDocumentList({
    filter,
    team,
    showAll,
    sortBy,
    sortOrder,
    offset,
    tags,
    scope,
  }: ILoadDocumentListPayload): Promise<IDocument[]> {
    const body: FormData = new FormData();
    body.append('filter', filter);
    body.append('team', team ? 'true' : 'false');
    body.append('showall', showAll ? 'true' : 'false');
    body.append('sortby', sortBy);
    body.append('sortorder', sortOrder.toLowerCase());
    body.append('limit', '100');
    body.append('offset', `${offset}`);
    body.append('tags', tags);
    if (scope.length) {
      scope.forEach((item: string) => {
        body.append('scope[]', item);
      });
    }
    const { data } = await Axios.post('/get/document/list', body);
    if (data.error) {
      throw new Error(data.error);
    }
    return data.documents.map(document => ({
      companyName: document.company_name,
      createdAt: document.created_at,
      expirationDate: document.expiration_date,
      externalId: document.external_id,
      id: document.id,
      isPrivate: document.is_private,
      isSigning: document.is_signing,
      name: document.name,
      recipients: document.recipients.map(recipient => ({
        companyName: recipient.company_name,
        companyNumber: recipient.company_number,
        email: recipient.email,
        firstName: recipient.first_name,
        gender: recipient.gender,
        lastName: recipient.last_name,
        note: recipient.note,
        recipientStatus: recipient.recipient_status,
        role: recipient.role,
        status: recipient.status,
        thumburl: recipient.thumb_url,
      })),
      sender: {
        name: document.sender_name,
        thumbUrl: document.sender_thumb,
      },
      signDate: document.sign_date,
      status: document.status,
      tags: document.tags,
      type: document.type,
      userId: document.user_id,
      value: document.value,
    }));
  }

  static async loadNotifications(documentId: string): Promise<IDocumentNotification[]> {
    const {
      data: { notifications, error },
    } = await Axios.get(`/get/document/${documentId}/notifications`);
    if (error) {
      throw new Error(error);
    }
    return notifications.map(notification => ({
      id: notification.id,
      user: {
        firstName: notification.first_name,
        fullName: notification.fullname,
        lastName: notification.last_name,
      },
      notificationNote: notification.note,
      notificationDate: notification.date,
      notificationType: notification.type,
    }));
  }

  static async deleteDocumentNotification(notificationId: string): Promise<string> {
    const body = new FormData();
    body.append('action', 'notification-delete');
    body.append('notification_id', notificationId);
    const {
      data: { document_id, error },
    } = await Axios.post(this.saveDocumentPath, body);
    if (error) {
      throw new Error(error);
    }
    return document_id;
  }

  static async addDocumentNotification(
    payload: IDocumentNotificationPayload,
    documentId: string
  ): Promise<string> {
    const body = new FormData();
    body.append('action', 'notification');
    body.append('notification-date', payload.date);
    body.append('notification-type', payload.type);
    body.append('notification-user_id', payload.userId);
    body.append('notification-note', payload.note);
    body.append('document-id', documentId);
    const {
      data: { document_id, error },
    } = await Axios.post(this.saveDocumentPath, body);
    if (error) {
      throw new Error(error);
    }
    return document_id;
  }

  static async documentUpdated(documentId: string): Promise<boolean> {
    const {
      data: { documentUpdated },
    } = await apolloClient.mutate({
      mutation: DocumentMutations.documentUpdatedMutation,
      variables: { documentId },
    });
    return documentUpdated;
  }
  static async getDocumentStatus(documentId: string): Promise<string> {
    const {
      data: { document },
    } = await apolloClient.query({
      query: DocumentQueries.DocumentStatusQuery,
      variables: { documentId },
      fetchPolicy: EFetchPolicy.NetworkOnly,
    });
    return document.status;
  }
}
